import React, {useMemo} from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, DropdownList, FlexRow, Icon} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {orderedDataColumnIds} from './ReportTable/useReportTableColumns';
import {labelForField} from './ReportTable/fieldLabels';
import {fieldByKey, ReportTaxonomy} from './contracts/report-taxonomy.types';
import {grainOf, ReportGrain} from './reportGrain';

export interface ColumnManagerProps {
	fieldLabels?: Record<string, string>;
}

/** The task grain's offer list, kept deliberately short. The dropdown is a visibility toggle over
 * the definition's own columns, so a column absent from `fields` has no way into a report otherwise
 * — the AI has to be asked for it. Deliverable is the one users ask for by hand, so it is offered;
 * the rest of the task catalog is not, because every offered column that gets ticked is another
 * path added to the fetch. */
const TASK_OFFERABLE_COLUMN_IDS = ['task.deliverable.name'];

/** Which columns a grain offers a reader that its report is not already showing.
 *
 * The two grains answer differently, on purpose.
 *
 * TASK_REPORT offers the deliverable and nothing else, for the reason above.
 *
 * PROJECT_STATUS offers everything its catalog publishes except company-defined custom fields.
 * That grain's catalog carries 21 fields against 10 `defaultColumns`, and the eleven left over —
 * the six retainer figures, the status note, priority, labels, and the billable / non-billable
 * split — had no way into a report at all. Reading them off the catalog rather than naming them
 * here is what keeps a twelfth field, the day report-service publishes one, from needing a change
 * in this file. Custom fields are the one exclusion: report-service merges them into the same
 * array, but nothing resolves a row value at their keys on this grain.
 *
 * One product decision is recorded here rather than only in the tracker: the retainer columns
 * are added BY HAND. The original requirements had them appear on their own once a reader narrowed
 * to retainer projects. That would make the definition mutate as a side effect of filtering, which
 * needs its own answers for a reader who then unticks one and for the filter being removed again —
 * so it is accepted for this release that a reader ticks them, and worth revisiting only if
 * readers ask for the automatic behaviour. */
function offerableColumnIds(grain: ReportGrain | null, taxonomy: ReportTaxonomy): string[] {
	if (grain === 'PROJECT_STATUS') {
		// Company-defined custom fields are merged into the same `fields` array by report-service,
		// but nothing resolves a row value at their keys on this grain, so a ticked one would render
		// the N/A sentinel in every row forever — under its correct heading, since the label comes
		// from the same catalog, which makes it read as real data rather than an obvious fault. It
		// also survives save and reopen. They stay legitimately useful for Group By, which is why
		// this is filtered at the point of offering rather than dropped when the catalog is parsed.
		return taxonomy.fields.filter(field => !field.custom).map(field => field.key);
	}
	if (grain === 'TASK_REPORT') {
		return TASK_OFFERABLE_COLUMN_IDS;
	}
	// A grain this builder does not build offers nothing at all, rather than some other grain's
	// columns — the same fail-closed posture `grainOf`'s null already gets from the conditions editor.
	return [];
}

/** Merges the definition's canonical leaf-column order with the current `tableState.columnOrder`:
 * current order wins for columns already present (so user reordering persists across renders),
 * any newly-added field path (not yet in `columnOrder`, e.g. after an AI patch) is appended at the
 * end in canonical order. Fixed (`__`-prefixed) columns are excluded — they are never toggleable
 * and cannot be reordered. */
function resolveColumnIds(definitionFieldPaths: string[], currentOrder: string[]): string[] {
	const currentDataIds = currentOrder.filter(id => !id.startsWith('__'));
	const currentSet = new Set(currentDataIds);
	const missing = definitionFieldPaths.filter(id => !currentSet.has(id));
	return [...currentDataIds, ...missing];
}

/** DS DropdownList renders its root as a flex:1 item, which grows to fill the controls row and
 * left-aligns the trigger (defeating the row's justify-content:flex-end). Wrap it in a hugging
 * inline-flex container (flex:0 0 auto) so the Columns button stays content-width and the Group By
 * + Columns cluster sits at the right, as in the design. (GroupByControl gets this from its FlexColumn.) */
const ColumnsTriggerWrapper = styled.div`
	display: inline-flex;
	flex: 0 0 auto;
`;

/** Columns dropdown: one checkbox row per leaf data column (visibility toggle). Fixed
 * structural columns (expander/favourite) are never listed. Column reordering is available via
 * dragging table headers. Operates through `useReportBuilder()`'s `tableState`/`updateTableState`,
 * plus `addColumn` for the offered columns below.
 *
 * Two kinds of row are listed. The report's own columns behave as they always have: checked when
 * visible, and ticking toggles visibility. An OFFERABLE column the report does not carry is listed
 * unchecked, and ticking it adds it to the definition — after which it is an ordinary column of the
 * first kind. Offers are gated on the taxonomy actually publishing the field, so a report-service
 * that has not shipped one shows no dead option. */
export function ColumnManager({fieldLabels}: ColumnManagerProps): JSX.Element {
	const {formatMessage} = useIntl();
	const {definition, tableState, updateTableState, taxonomy, addColumn} = useReportBuilder();

	const columnIds = useMemo(
		() => resolveColumnIds(orderedDataColumnIds(definition), tableState.columnOrder),
		[definition, tableState.columnOrder]
	);

	const offeredIds = useMemo(() => {
		if (!taxonomy) {
			return [];
		}
		const present = new Set(columnIds);
		// The `fieldByKey` gate is what keeps a report-service that has not shipped a hand-named
		// column from showing a dead option. It is trivially satisfied on the project-status path,
		// whose ids came off this same catalog, and is left in rather than branched around so both
		// grains reach the dropdown through one filter.
		return offerableColumnIds(grainOf(definition), taxonomy).filter(
			id => !present.has(id) && fieldByKey(taxonomy, id) !== undefined
		);
	}, [taxonomy, columnIds, definition]);

	const toggleVisibility = (columnId: string): void => {
		const isVisible = tableState.columnVisibility[columnId] !== false;
		updateTableState({
			columnVisibility: {...tableState.columnVisibility, [columnId]: !isVisible},
		});
	};

	return (
		<ColumnsTriggerWrapper>
			<DropdownList>
				<DropdownList.Trigger>
					{/* the design: Columns is the neutral field-style control (grey border #6b6d7b,
				    dark #232334 text, leading list glyph + built-in trailing chevron) — the same
				    "Filter" component as Download, NOT a brand-blue ButtonDropdown. aria-label carries
				    the accessible name since the label sits inside a FlexRow rather than as text. */}
					<ButtonDropdownField
						width={'fit-content'}
						aria-label={formatMessage({id: 'ai_report_builder.columns'})}
						data-cy={'ai-report-column-manager'}
					>
						<FlexRow alignItems={'center'} gap={'xs'}>
							<Icon icon={'list'} newColor={'onlight'} size={'s'} />
							<FormattedMessage id={'ai_report_builder.columns'} />
						</FlexRow>
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					{columnIds.map(columnId => (
						<DropdownList.CheckboxItem
							key={columnId}
							checked={tableState.columnVisibility[columnId] !== false}
							onChange={() => toggleVisibility(columnId)}
							data-cy={`ai-report-column-toggle-${columnId}`}
						>
							{/* The same resolution the table's headings use, so a column is called one
							thing in both places. */}
							{labelForField(columnId, taxonomy, fieldLabels)}
						</DropdownList.CheckboxItem>
					))}
					{offeredIds.map(columnId => (
						<DropdownList.CheckboxItem
							key={columnId}
							checked={false}
							onChange={() => addColumn(columnId)}
							data-cy={`ai-report-column-toggle-${columnId}`}
						>
							{labelForField(columnId, taxonomy, fieldLabels)}
						</DropdownList.CheckboxItem>
					))}
				</DropdownList.Content>
			</DropdownList>
		</ColumnsTriggerWrapper>
	);
}
